import { displayName } from '../../types';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Paper,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  List, ListItem, ListItemButton, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Chip, Divider, LinearProgress, Tooltip
} from '@mui/material';
import {
  Upload, Delete, Download, Description, Badge, Gavel,
  School, FlightTakeoff, Receipt, Folder, Search
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { apiFetch } from '../../services/apiFetch';

const CATEGORIES = [
  { value: 'id', label: 'ID Document', icon: <Badge />, color: '#1976d2' },
  { value: 'contract', label: 'Contract', icon: <Gavel />, color: '#7b1fa2' },
  { value: 'certificate', label: 'Certificate', icon: <School />, color: '#388e3c' },
  { value: 'visa', label: 'Visa / Work Permit', icon: <FlightTakeoff />, color: '#f57c00' },
  { value: 'payslip', label: 'Payslip', icon: <Receipt />, color: '#0288d1' },
  { value: 'other', label: 'Other', icon: <Folder />, color: '#616161' },
];

const getCategoryInfo = (value: string) => CATEGORIES.find(c => c.value === value) || CATEGORIES[5];

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Documents: React.FC = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    apiService.getEmployees().then(setEmployees).catch(() => setError('Failed to load employees'));
  }, []);

  const loadDocuments = useCallback(async (employeeId: string) => {
    setLoading(true);
    try {
      const docs = await apiService.getEmployeeDocuments(employeeId);
      setDocuments(docs);
    } catch { setError('Failed to load documents'); }
    finally { setLoading(false); }
  }, []);

  const handleSelectEmployee = (emp: any) => {
    setSelectedEmployee(emp);
    setDocuments([]);
    loadDocuments(emp.id);
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedEmployee) return;
    setUploading(true);
    try {
      await apiService.uploadDocument(selectedEmployee.id, uploadFile, uploadCategory, uploadDescription);
      setSuccess('File uploaded successfully!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      setUploadCategory('other');
      loadDocuments(selectedEmployee.id);
    } catch { setError('Upload failed. Please try again.'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (docId: string, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await apiService.deleteDocument(docId);
      setSuccess('Document deleted.');
      loadDocuments(selectedEmployee.id);
    } catch { setError('Delete failed.'); }
  };

  const handleDownload = async (docId: string, originalName: string) => {
    try {
      const url = apiService.getDocumentDownloadUrl(docId);
      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalName;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      alert(`Failed to download document: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) { setUploadFile(file); setUploadOpen(true); }
  };

  const filteredEmployees = employees.filter(e =>
    displayName(e).toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.department?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDocs = documents.filter(d => !filterCategory || d.category === filterCategory);

  const docCountByCategory = (empId: string) => documents.length;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>Employee Documents</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Employee List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
            <Box p={2} borderBottom="1px solid #eee">
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>Employees</Typography>
              <TextField
                size="small" fullWidth placeholder="Search..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              <List dense disablePadding>
                {filteredEmployees.filter(e => e.employmentStatus === 'active').map(emp => (
                  <ListItem key={emp.id} disablePadding sx={{ borderBottom: '1px solid #f5f5f5' }}>
                  <ListItemButton
                    selected={selectedEmployee?.id === emp.id}
                    onClick={() => handleSelectEmployee(emp)}
                    sx={{ '&.Mui-selected': { bgcolor: 'primary.50' } }}
                  >
                    <ListItemIcon>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%', bgcolor: 'primary.main',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 'bold'
                      }}>
                        {(emp.nickname || emp.firstName)?.[0]}{emp.lastName?.[0]}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={displayName(emp)}
                      secondary={emp.department?.name}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: selectedEmployee?.id === emp.id ? 'bold' : 'normal' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Document Area */}
        <Grid size={{ xs: 12, md: 8 }}>
          {!selectedEmployee ? (
            <Paper sx={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, color: 'text.secondary' }}>
              <Folder sx={{ fontSize: 64, opacity: 0.3 }} />
              <Typography>Select an employee to view their documents</Typography>
            </Paper>
          ) : (
            <Paper sx={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
              {/* Header */}
              <Box p={2} borderBottom="1px solid #eee" display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">{displayName(selectedEmployee)}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedEmployee.department?.name} · {documents.length} document{documents.length !== 1 ? 's' : ''}</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} label="Filter">
                      <MenuItem value="">All</MenuItem>
                      {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" startIcon={<Upload />} onClick={() => setUploadOpen(true)}>Upload</Button>
                </Box>
              </Box>

              {/* Drop Zone hint */}
              <Box
                sx={{ mx: 2, mt: 1.5, p: 1.5, border: '2px dashed', borderColor: dragOver ? 'primary.main' : '#e0e0e0',
                  borderRadius: 2, textAlign: 'center', bgcolor: dragOver ? 'primary.50' : 'transparent', transition: 'all 0.2s', cursor: 'pointer' }}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => setUploadOpen(true)}
              >
                <Typography variant="caption" color="text.secondary">
                  <Upload fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Drag & drop files here or click to upload
                </Typography>
              </Box>

              {/* Category summary chips */}
              {documents.length > 0 && (
                <Box px={2} pt={1} display="flex" gap={1} flexWrap="wrap">
                  {CATEGORIES.filter(cat => documents.some(d => d.category === cat.value)).map(cat => (
                    <Chip key={cat.value} size="small" icon={cat.icon}
                      label={`${cat.label} (${documents.filter(d => d.category === cat.value).length})`}
                      onClick={() => setFilterCategory(filterCategory === cat.value ? '' : cat.value)}
                      variant={filterCategory === cat.value ? 'filled' : 'outlined'}
                      sx={{ borderColor: cat.color, color: filterCategory === cat.value ? 'white' : cat.color, bgcolor: filterCategory === cat.value ? cat.color : 'transparent' }}
                    />
                  ))}
                </Box>
              )}

              {/* Document list */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : filteredDocs.length === 0 ? (
                  <Box textAlign="center" py={6} color="text.secondary">
                    <Description sx={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography mt={1}>No documents yet. Upload the first one!</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {filteredDocs.map((doc, i) => {
                      const cat = getCategoryInfo(doc.category);
                      return (
                        <React.Fragment key={doc.id}>
                          {i > 0 && <Divider />}
                          <ListItem sx={{ py: 1.5, '&:hover': { bgcolor: '#f9f9f9' }, borderRadius: 1 }}>
                            <ListItemIcon>
                              <Box sx={{ color: cat.color }}>{cat.icon}</Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={<Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight="medium">{doc.originalName}</Typography>
                                <Chip label={cat.label} size="small" sx={{ fontSize: 10, height: 18, bgcolor: cat.color + '20', color: cat.color }} />
                              </Box>}
                              secondary={<>
                                {doc.description && <span>{doc.description} · </span>}
                                <span>{formatSize(doc.fileSize)}</span>
                                {doc.uploadedBy && <span> · Uploaded by {doc.uploadedBy}</span>}
                                <span> · {new Date(doc.createdAt).toLocaleDateString()}</span>
                              </>}
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Download">
                                <IconButton size="small" onClick={() => handleDownload(doc.id, doc.originalName)} sx={{ mr: 0.5 }}>
                                  <Download fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(doc.id, doc.originalName)} color="error">
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document{selectedEmployee ? ` — ${displayName(selectedEmployee)}` : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* File picker */}
          <Box
            sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input id="file-input" type="file" hidden onChange={e => setUploadFile(e.target.files?.[0] || null)}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
            {uploadFile ? (
              <Box>
                <Description color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="body2" fontWeight="bold">{uploadFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{formatSize(uploadFile.size)}</Typography>
              </Box>
            ) : (
              <Box>
                <Upload sx={{ fontSize: 40, color: '#ccc' }} />
                <Typography variant="body2" color="text.secondary">Click to select file</Typography>
                <Typography variant="caption" color="text.secondary">PDF, JPG, PNG, DOC, XLS (max 10MB)</Typography>
              </Box>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} label="Category">
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}><Box display="flex" alignItems="center" gap={1}><Box sx={{ color: c.color }}>{c.icon}</Box>{c.label}</Box></MenuItem>)}
            </Select>
          </FormControl>

          <TextField label="Description (optional)" value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} fullWidth multiline rows={2} />

          {uploading && <LinearProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading} startIcon={<Upload />}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
